/**
 * Offline cache for finalised inspection reports.
 *
 * On finalise we stash a small JSON snapshot of the inspection in localStorage and
 * the rendered branded PDF (binary) in the Cache Storage API. When the inspector
 * later opens the inspection page offline (dead-zone, plane, court room), the page
 * loads the cached snapshot + the cached PDF blob — they can still read the report
 * and re-download the file to send to the customer when they get signal again.
 *
 * Keys
 *   localStorage["psc.cachedInspection.{id}"]  = JSON snapshot
 *   localStorage["psc.cachedInspectionIds"]    = JSON array of ids
 *   Cache Storage "psc-finalised-pdfs"         = the GET /pdf and /notice-pdf responses
 */
import { API } from "@/lib/api";

const SNAPSHOT_PREFIX = "psc.cachedInspection.";
const INDEX_KEY = "psc.cachedInspectionIds";
const CACHE_NAME = "psc-finalised-pdfs";

const readIndex = () => {
  try { return JSON.parse(localStorage.getItem(INDEX_KEY) || "[]"); }
  catch { return []; }
};
const writeIndex = (ids) => {
  try { localStorage.setItem(INDEX_KEY, JSON.stringify(ids)); } catch { /* quota */ }
};

/** Persist a slim snapshot of the inspection (no photos — they bloat localStorage). */
export const snapshotInspection = (insp) => {
  if (!insp?.id) return;
  const snap = {
    id: insp.id,
    title: insp.title || "",
    status: insp.status,
    ai_report: insp.ai_report,
    ai_report_finalized: !!insp.ai_report_finalized,
    ai_report_finalized_at: insp.ai_report_finalized_at,
    ai_summary: insp.ai_summary,
    flagged_count: insp.flagged_count || 0,
    actions_count: insp.actions_count || 0,
    score_obtained: insp.score_obtained || 0,
    score_total: insp.score_total || 0,
    locked: !!insp.locked,
    locked_at: insp.locked_at,
    cached_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(SNAPSHOT_PREFIX + insp.id, JSON.stringify(snap));
    const ids = new Set(readIndex());
    ids.add(insp.id);
    writeIndex([...ids]);
  } catch (e) {
    // ignore quota — best-effort
    console.warn("[offlineReports] snapshot save failed", e);
  }
};

export const getCachedInspection = (id) => {
  try {
    const raw = localStorage.getItem(SNAPSHOT_PREFIX + id);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

export const listCachedInspections = () => {
  return readIndex()
    .map((id) => getCachedInspection(id))
    .filter(Boolean)
    .sort((a, b) => (b.cached_at || "").localeCompare(a.cached_at || ""));
};

export const removeCachedInspection = async (id) => {
  try { localStorage.removeItem(SNAPSHOT_PREFIX + id); } catch { /* noop */ }
  writeIndex(readIndex().filter((x) => x !== id));
  try {
    if ("caches" in window) {
      const cache = await caches.open(CACHE_NAME);
      const keys = await cache.keys();
      await Promise.all(
        keys.filter((req) => req.url.includes(`/inspections/${id}/`)).map((req) => cache.delete(req))
      );
    }
  } catch { /* noop */ }
};

/** Cache the branded inspection PDF. Best-effort (Notice PDF is a POST so isn't cached). */
export const cacheReportPdfs = async (id) => {
  if (!("caches" in window)) return { pdf: false };
  const token = localStorage.getItem("psc_token");
  if (!token) return { pdf: false };
  const cache = await caches.open(CACHE_NAME);
  try {
    const res = await fetch(`${API}/inspections/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return { pdf: false };
    await cache.put(new Request(`${API}/inspections/${id}/pdf`), res.clone());
    return { pdf: true };
  } catch (e) {
    console.warn("[offlineReports] failed to cache pdf", e);
    return { pdf: false };
  }
};

/** Return cached PDF as a Blob if available. */
export const getCachedPdf = async (id) => {
  if (!("caches" in window)) return null;
  const cache = await caches.open(CACHE_NAME);
  const res = await cache.match(`${API}/inspections/${id}/pdf`);
  if (!res) return null;
  return await res.blob();
};
